import { globalTheme } from '@frontend/components/external-providers';
import { AddBlockIcon } from '@frontend/components/ui';
import {
  useCanvasAdd,
  useCanvasCollect,
  useCanvasFlow,
  useCanvasRemove,
  useModal,
  usePostCommentReply,
  useProjectPermission,
} from '@frontend/editor/data-access';
import {
  EditorCtx,
  UICtx as EditorUICtx,
} from '@frontend/editor/external-providers';
import {
  ActionCellTypesEnum,
  CouponTypesEnum,
  DrawerTypesEnum,
  ElementTypesEnum,
  EntryPointsEnum,
  ModalTypesEnum,
  ResponseWithData,
} from '@frontend/editor/interface';
import { UICtx } from '@frontend/sorghum/external-providers';
import {
  HOW_TO_USE_ATTRIBUTE_EN,
  PAGE_COMMENT_AUTO_REPLY,
  sendGAEvent,
  usePath,
} from '@frontend/sorghum/utils';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import BookmarkRemoveOutlinedIcon from '@mui/icons-material/BookmarkRemoveOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import CommentBankOutlinedIcon from '@mui/icons-material/CommentBankOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import TextIcon from '@mui/icons-material/FormatColorText';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LabelOffOutlinedIcon from '@mui/icons-material/LabelOffOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ModeIcon from '@mui/icons-material/Mode';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box, Button, ClickAwayListener, Link, Popper } from '@mui/material';
import { get } from 'lodash';
import { MouseEvent, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactFlow, { SelectionMode } from 'reactflow';
import 'reactflow/dist/style.css';
import styled from 'styled-components';
import { ConfirmCloseDrawerModal } from '../confirm-close-drawer-modal/confirm-close-drawer-modal';
import ConnectionLineComponent from '../connection-line-component/connection-line-component';
import Control from '../control/control';
import Dialog from '../dialog/dialog';
import EditorCanvasContainer from '../editor-canvas-container/editor-canvas-container';
import CustomEdge, {
  CustomFocusMarker,
  CustomMarker,
} from '../editor-custom-edge/editor-custom-edge';
import EditorDrawerGroup from '../editor-drawer-group/editor-drawer-group';
import EditorLoadingModal from '../editor-loading-modal/editor-loading-modal';
import EditorMenu from '../editor-menu/editor-menu';
import { RedeemCouponIcon, SendCouponIcon, ShowCouponIcon } from '../icons';
import NodeAction from '../node-action/node-action';
import NodeBlock from '../node-block/node-block';
import NodeCondition from '../node-condition/node-condition';
import NodeContextMenu from '../node-context-menu/node-context-menu';
import NodeCoupon from '../node-coupon/node-coupon';
import NodeCreateBlockMenu from '../node-create-block-menu/node-create-block-menu';
import NodeEntryPoint from '../node-entry-point/node-entry-point';
import NodeFloatPanel from '../node-float-panel/node-float-panel';
import { NodeJumpToFlow } from '../node-jump-to-flow/node-jump-to-flow';
import NodeNote from '../node-note/node-note';
import NodeShareLink from '../node-share-link/node-share-link';

const nodeTypes = {
  BLOCK: NodeBlock,
  ENTRY_POINT: NodeEntryPoint,
  CREATE_MENU: NodeCreateBlockMenu,
  SHARE_LINK: NodeShareLink,
  CONDITION: NodeCondition,
  COUPON: NodeCoupon,
  ACTION: NodeAction,
  NOTE: NodeNote,
  JUMP_TO_FLOW: NodeJumpToFlow,
};

const edgeTypes = {
  CUSTOM: CustomEdge,
};

const AddButtonContainerStyled = styled(Box)(({ theme }) => ({
  top: 0,
  right: '20px',
  display: 'flex',
  width: '36px',
  padding: '0',
  height: '100%',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'absolute',
  zIndex: 5,
}));

interface FlowProps {
  flowID: string;
  flowName?: string;
  welcomeMessagePage: string;
  defaultAnswerPage: string;
  persistentMenuPage: string;
  updateFlowChart: () => void;
  getFlowDataInPersistentMenu: () => void;
}

export const Flow = ({
  flowID,
  flowName,
  welcomeMessagePage,
  defaultAnswerPage,
  persistentMenuPage,
  updateFlowChart,
  getFlowDataInPersistentMenu,
}: FlowProps) => {
  const {
    nodes,
    edges,
    targetElement,
    onConnectStart,
    onConnectEnd,
    onNodesChange,
    onEdgesChange,
    handleEdgeDelete,
    handleNodesDelete,
    handleEdgeUpdate,
    handleNodesDragEnd,
    handleOnSelectionContextMenu,
    handleOnNodeContextMenu,
  } = useCanvasFlow();
  const { navigateToProjectPage } = usePath();
  const { removeTargetBlock, removeSelectedBlocks, removeElement } =
    useCanvasRemove();
  const {
    addSendCoupon,
    addShowCoupon,
    addRedeemCoupon,
    addTextButton,
    addRecurringNotification,
    addImage,
    addGallery,
    addCondition,
    addAction,
    addActionSetAttribute,
    addActionRemoveAttribute,
    addActionSubscribeSequence,
    addActionUnsubscribeSequence,
    addActionExportGoogleSheet,
    addBlock,
    addNote,
    addCollectUserAnswer,
    addJumpToFlow,
  } = useCanvasAdd();
  const { openModal, modalState, closeModal } = useModal();
  const { isTemplateEditor: isTemplateMode } = useProjectPermission();
  const { mutate: addCommentAutoReply } = usePostCommentReply();
  const { selectedNodes } = useCanvasCollect();

  const [t] = useTranslation();
  const {
    isFlowDraggable,
    isFlowPreventScrolling,
    isFlowSelectable,
    isDrawerUpdate,
    isFlowZoomOnScroll,
    setOnFocusCellID,
    drawerCallback,
    isDrawerFullyUpdate,
  } = useContext(EditorCtx);
  const uiState = useContext(UICtx);
  const {
    drawerType,
    openModal: openEditorModal,
    closeDrawer,
  } = useContext(EditorUICtx);

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const onAddButtonClick = useCallback(
    (addType: ElementTypesEnum | CouponTypesEnum) => {
      setIsMenuOpen(false);
      switch (addType) {
        case ElementTypesEnum.TEXT_BUTTON: {
          const blockID = addBlock(undefined, undefined, false);
          addTextButton(blockID);
          break;
        }
        case ElementTypesEnum.RECURRING_NOTIFICATION: {
          const blockID = addBlock(undefined, undefined, false);
          addRecurringNotification(blockID);
          break;
        }
        case ElementTypesEnum.IMAGE: {
          const blockID = addBlock(undefined, undefined, false);
          addImage(blockID);
          break;
        }
        case ElementTypesEnum.GALLERY: {
          const blockID = addBlock(undefined, undefined, false);
          addGallery(blockID);
          break;
        }
        case ElementTypesEnum.SHARE_LINK: {
          break;
        }
        case CouponTypesEnum.SEND: {
          addSendCoupon(undefined, undefined, false);
          break;
        }
        case CouponTypesEnum.SHOW: {
          addShowCoupon(undefined, undefined, false);
          break;
        }
        case CouponTypesEnum.REDEEM: {
          addRedeemCoupon(undefined, undefined, false);
          break;
        }
        case ElementTypesEnum.CONDITION: {
          addCondition(undefined, undefined, false);
          break;
        }
        case ElementTypesEnum.NOTE: {
          addNote(undefined, false);
          break;
        }
        case ElementTypesEnum.COLLECT_USER_ANSWER: {
          const blockID = addBlock(undefined, undefined, false);
          addCollectUserAnswer(blockID);
          break;
        }
        case ElementTypesEnum.JUMP_TO_FLOW: {
          addJumpToFlow(undefined, undefined, false);
        }
      }
    },
    [
      addBlock,
      addTextButton,
      addRecurringNotification,
      addImage,
      addGallery,
      addSendCoupon,
      addShowCoupon,
      addRedeemCoupon,
      addCondition,
      addNote,
      addCollectUserAnswer,
      addJumpToFlow,
    ],
  );

  const onAddActionButtonClick = useCallback(
    (actionType: ActionCellTypesEnum) => {
      setIsMenuOpen(false);
      const actionID = addAction(undefined, undefined, false);
      switch (actionType) {
        case ActionCellTypesEnum.SET: {
          addActionSetAttribute(actionID);
          break;
        }
        case ActionCellTypesEnum.REMOVE: {
          addActionRemoveAttribute(actionID);
          break;
        }
        case ActionCellTypesEnum.SUBSCRIBE_SEQUENCE: {
          addActionSubscribeSequence(actionID);
          break;
        }
        case ActionCellTypesEnum.UNSUBSCRIBE_SEQUENCE: {
          addActionUnsubscribeSequence(actionID);
          break;
        }
        case ActionCellTypesEnum.EXPORT_GOOGLE_SHEET: {
          addActionExportGoogleSheet(actionID);
          break;
        }
      }
    },
    [
      addAction,
      addActionSetAttribute,
      addActionRemoveAttribute,
      addActionSubscribeSequence,
      addActionUnsubscribeSequence,
      addActionExportGoogleSheet,
    ],
  );

  const handleDeleteBlockModal = useCallback(() => {
    removeTargetBlock();
    closeModal();
  }, [removeTargetBlock, closeModal]);

  const handleDeleteBlocksModal = useCallback(() => {
    removeSelectedBlocks();
    closeModal();
  }, [removeSelectedBlocks, closeModal]);

  const handleDeleteTextButtonModal = useCallback(() => {
    removeElement();
    closeModal();
  }, [removeElement, closeModal]);

  const handleDeleteElementModal = useCallback(() => {
    removeElement();
    closeModal();
  }, [removeElement, closeModal]);

  const gotoWelcomeMessage = useCallback(() => {
    closeModal();
    updateFlowChart();
    navigateToProjectPage({ path: welcomeMessagePage });
  }, [closeModal, navigateToProjectPage, updateFlowChart, welcomeMessagePage]);

  const gotoBasicSetting = useCallback(() => {
    closeModal();
    updateFlowChart();
    switch (modalState.entryType) {
      case EntryPointsEnum.DEFAULT_ANSWER: {
        navigateToProjectPage({ path: defaultAnswerPage });
        break;
      }
      case EntryPointsEnum.PERSISTENT_MENU: {
        navigateToProjectPage({ path: persistentMenuPage });
        break;
      }
      case EntryPointsEnum.WELCOME_MESSAGE: {
        navigateToProjectPage({ path: welcomeMessagePage });
        break;
      }
    }
  }, [
    closeModal,
    defaultAnswerPage,
    modalState.entryType,
    navigateToProjectPage,
    persistentMenuPage,
    updateFlowChart,
    welcomeMessagePage,
  ]);

  const gotoPersistentMenu = useCallback(() => {
    closeModal();
    updateFlowChart();
    navigateToProjectPage({ path: persistentMenuPage });
    getFlowDataInPersistentMenu();
  }, [
    closeModal,
    getFlowDataInPersistentMenu,
    navigateToProjectPage,
    persistentMenuPage,
    updateFlowChart,
  ]);

  const handleAddButtonClick = useCallback(() => {
    setIsMenuOpen(true);
    // 清空 focus 目標避免 drawer 無法在新增後開啟
    setOnFocusCellID('');
  }, [setOnFocusCellID]);

  const handleAddCommentAutoReply = useCallback(() => {
    addCommentAutoReply(
      {
        name: `${flowName} ${t(
          'canvas.modal.addCommentAutoReply.defaultName',
        )}`,
        conditionName: t('canvas.blockMenu.condition'),
        flowId: flowID,
        flowName: flowName,
      },
      {
        onSuccess: (res: ResponseWithData<{ id: string }>) => {
          closeModal();
          if (res.code === 20000 && res.data.id) {
            sendGAEvent(
              'Comment auto reply',
              'comment auto reply - created',
              'Comment auto reply - created',
              '',
            );
            navigateToProjectPage({
              path: `${PAGE_COMMENT_AUTO_REPLY}/${res.data.id}`,
            });
            uiState.openRenameCommentAutoReplyModal(res.data.id);
          }
        },
      },
    );
  }, [
    addCommentAutoReply,
    closeModal,
    flowID,
    flowName,
    navigateToProjectPage,
    t,
    uiState,
  ]);

  // 點擊 flow 時都會清空 focusID，利用事件捕獲機制確認現在 focus 的目標是誰
  const handleFocus = useCallback(
    (e: MouseEvent) => {
      // 捕捉 onClickAway drawer 的時機，判斷是否要跳出警告未儲存的 modal
      if (drawerType !== DrawerTypesEnum.CLOSE) {
        // 判斷內容有沒有更新
        if (isDrawerUpdate) {
          e.preventDefault();
          e.stopPropagation();
          openEditorModal(ModalTypesEnum.CLOSE_UNSAVED_DRAWER);
        } else {
          setOnFocusCellID('');
          closeDrawer();
        }
      } else {
        setOnFocusCellID('');
        closeDrawer();
      }
    },
    [
      closeDrawer,
      drawerType,
      openEditorModal,
      isDrawerUpdate,
      setOnFocusCellID,
    ],
  );

  const handleCloseAndSaveDrawer = useCallback(() => {
    if (drawerCallback) {
      drawerCallback();
    }
  }, [drawerCallback]);

  const editorMenuItems = [
    {
      title: t('canvas.contentTitle'),
      dropdownItems: [
        {
          id: 'option_text_button',
          onClick: () => onAddButtonClick(ElementTypesEnum.TEXT_BUTTON),
          icon: <TextIcon color="primary" />,
          title: t('canvas.blockMenu.contentButton'),
        },
        {
          id: 'option_image',
          icon: <ImageOutlinedIcon color="primary" />,
          title: t('canvas.blockMenu.image'),
          onClick: () => onAddButtonClick(ElementTypesEnum.IMAGE),
        },
        {
          id: 'option_gallery',
          icon: <CollectionsOutlinedIcon color="primary" />,
          title: t('canvas.blockMenu.gallery'),
          onClick: () => onAddButtonClick(ElementTypesEnum.GALLERY),
          tooltip: <Box>{t('canvas.blockMenu.showGalleryTooltip')}</Box>,
        },
        {
          id: 'option_theme_subscription',
          icon: <NotificationsNoneIcon color="primary" />,
          title: t('canvas.blockMenu.recurringNotificationsButton'),
          onClick: () =>
            onAddButtonClick(ElementTypesEnum.RECURRING_NOTIFICATION),
          tooltip: <Box>{t('canvas.blockMenu.rnTooltip')}</Box>,
        },
        {
          id: 'option_collect_user_answer',
          icon: <CommentBankOutlinedIcon color="primary" />,
          title: t('canvas.blockMenu.collectUserAnswerButton'),
          onClick: () => onAddButtonClick(ElementTypesEnum.COLLECT_USER_ANSWER),
          tooltip: (
            <Box>{t('canvas.blockMenu.showCollectUserAnswerTooltip')}</Box>
          ),
        },
      ],
    },
    {
      title: t('canvas.marketingTool'),
      dropdownItems: [
        // {
        //   onClick: () => onAddButtonClick(ElementTypesEnum.SHARE_LINK),
        //   icon: (
        //     <ShareIcon
        //       sx={{
        //         color: globalTheme.palette?.grass?.[600],
        //       }}
        //     />
        //   ),
        //   title: t('canvas.blockMenu.shareLink'),
        // },
        {
          id: 'option_send_coupon',
          onClick: () => onAddButtonClick(CouponTypesEnum.SEND),
          icon: (
            <SendCouponIcon
              sx={{
                color: globalTheme.palette?.grass?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.sendCoupon'),
          tooltip: <Box>{t('canvas.blockMenu.sendCouponTooltip')}</Box>,
        },
        {
          id: 'option_show_coupon',
          onClick: () => onAddButtonClick(CouponTypesEnum.SHOW),
          icon: (
            <ShowCouponIcon
              sx={{
                color: globalTheme.palette?.grass?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.showCoupon'),
          tooltip: <Box>{t('canvas.blockMenu.showCouponTooltip')}</Box>,
        },
        {
          id: 'option_redeem_coupon',
          onClick: () => onAddButtonClick(CouponTypesEnum.REDEEM),
          icon: (
            <RedeemCouponIcon
              sx={{
                color: globalTheme.palette?.grass?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.redeemCoupon'),
          tooltip: <Box>{t('canvas.blockMenu.redeemCouponTooltip')}</Box>,
        },
      ],
    },
    {
      title: t('canvas.blockMenu.action'),
      dropdownItems: [
        {
          id: 'option_set_attribute',
          onClick: () => onAddActionButtonClick(ActionCellTypesEnum.SET),
          icon: (
            <BookmarkBorderOutlinedIcon
              sx={{
                color: globalTheme.palette?.purple?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.setAttribute'),
          tooltip: (
            <Box>
              {t('canvas.blockMenu.setAttributeTooltip')}{' '}
              <Link
                sx={{
                  color: get(globalTheme, 'palette.infoOnTooltips.main', ''),
                  cursor: 'pointer',
                }}
                onClick={() => window.open(HOW_TO_USE_ATTRIBUTE_EN, '_blank')}
              >
                {t('canvas.blockMenu.setAttributeTooltipLink')}
              </Link>
            </Box>
          ),
        },
        {
          id: 'option_remove_attribute',
          onClick: () => onAddActionButtonClick(ActionCellTypesEnum.REMOVE),
          icon: (
            <BookmarkRemoveOutlinedIcon
              sx={{
                color: globalTheme.palette?.purple?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.removeAttribute'),
        },
        {
          id: 'option_subscribe_sequence',
          onClick: () =>
            onAddActionButtonClick(ActionCellTypesEnum.SUBSCRIBE_SEQUENCE),
          icon: (
            <LabelOutlinedIcon
              sx={{
                color: globalTheme.palette?.purple?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.subscribeSequence'),
        },
        {
          id: 'option_unsubscribe_sequence',
          onClick: () =>
            onAddActionButtonClick(ActionCellTypesEnum.UNSUBSCRIBE_SEQUENCE),
          icon: (
            <LabelOffOutlinedIcon
              sx={{
                color: globalTheme.palette?.purple?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.unsubscribeSequence'),
        },
        {
          id: 'option_google_sheet',
          onClick: () =>
            onAddActionButtonClick(ActionCellTypesEnum.EXPORT_GOOGLE_SHEET),
          icon: (
            <TableChartOutlinedIcon
              sx={{
                color: globalTheme.palette?.purple?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.exportGoogleSheet'),
          tooltip: <Box>{t('canvas.blockMenu.exportGoogleSheetTooltip')}</Box>,
        },
      ],
    },
    {
      title: t('canvas.condition.title'),
      dropdownItems: [
        {
          id: 'option_condition',
          onClick: () => onAddButtonClick(ElementTypesEnum.CONDITION),
          icon: (
            <FilterAltOutlinedIcon
              sx={{
                color: globalTheme.palette?.orange?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.condition'),
        },
        {
          id: 'option_jump_to_flow',
          onClick: () => onAddButtonClick(ElementTypesEnum.JUMP_TO_FLOW),
          icon: (
            <AccountTreeOutlinedIcon
              sx={{
                color: globalTheme.palette?.orange?.[600],
              }}
            />
          ),
          title: t('canvas.blockMenu.jumpToFlow'),
          tooltip: <Box>{t('canvas.blockMenu.jumpToFlowTooltip')}</Box>,
        },
      ],
    },
    ...(isTemplateMode
      ? [
          {
            title: 'template',
            dropdownItems: [
              {
                onClick: () => onAddButtonClick(ElementTypesEnum.NOTE),
                icon: (
                  <ModeIcon
                    sx={{
                      color: globalTheme.palette?.pink?.[600],
                    }}
                  />
                ),
                title: 'Note',
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <EditorCanvasContainer>
      <EditorLoadingModal />
      <EditorDrawerGroup />
      <NodeFloatPanel />

      <ReactFlow
        // 在捕獲階段才設置，node focus 的事件也會在 clickCapture 觸發，才會是先清除在設定目標的正確順序
        onClickCapture={handleFocus}
        nodes={nodes}
        edges={edges}
        selectionMode={SelectionMode.Partial}
        onConnectEnd={onConnectEnd}
        onConnectStart={onConnectStart}
        onClickConnectStart={onConnectStart}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeContextMenu={handleOnNodeContextMenu}
        onSelectionContextMenu={handleOnSelectionContextMenu}
        onSelectionDragStop={(_, nodes) => handleNodesDragEnd(nodes)}
        onNodesDelete={handleNodesDelete}
        onNodeDragStop={(_, node, nodes) => handleNodesDragEnd(nodes)}
        onEdgesDelete={handleEdgeDelete}
        onEdgeUpdate={handleEdgeUpdate}
        nodeTypes={nodeTypes}
        // 預設的 edge 樣式
        defaultEdgeOptions={{
          type: 'CUSTOM',
        }}
        minZoom={0.1}
        edgeTypes={edgeTypes}
        // 設定拉線時的樣式
        connectionLineComponent={ConnectionLineComponent}
        // 設定在 empty area 會是 pan entire canvas 還是 select node or edge
        selectionOnDrag={isFlowSelectable}
        selectNodesOnDrag={isFlowSelectable}
        panOnDrag={isFlowDraggable && !isFlowSelectable}
        nodesDraggable={isFlowDraggable}
        // 門檻 1px：純點擊走 click 選取（含 nodrag 區），也避免按下瞬間就起拖。
        // 框選模式（isFlowSelectable）例外：門檻 >0 會讓 reactflow 在 click 也呼叫 addSelectedNodes([id])，
        // 點一下已框選的節點會把多選收斂成單選，所以維持 0 走原本的 drag-start 選取
        nodeDragThreshold={isFlowSelectable ? 0 : 1}
        preventScrolling={isFlowPreventScrolling}
        zoomOnScroll={isFlowZoomOnScroll}
        zoomOnDoubleClick={false}
        deleteKeyCode={null}
      >
        <Control addBlock={addBlock} />
      </ReactFlow>

      {/* 右鍵選單 */}
      <NodeContextMenu />
      <AddButtonContainerStyled>
        <Button
          id="canvas_element"
          ref={addButtonRef}
          sx={{
            minWidth: '36px',
          }}
          color="primary"
          variant="contained"
          onClick={handleAddButtonClick}
        >
          <AddBlockIcon />
        </Button>
      </AddButtonContainerStyled>
      {isMenuOpen && (
        <ClickAwayListener onClickAway={() => setIsMenuOpen(false)}>
          <Popper
            sx={{
              zIndex: 100,
            }}
            open={isMenuOpen}
            anchorEl={addButtonRef.current}
            placement="left-start"
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  altAxis: true,
                  altBoundary: true,
                  tether: true,
                  rootBoundary: 'document',
                  padding: '8px',
                },
              },
            ]}
          >
            <EditorMenu itemList={editorMenuItems} />
          </Popper>
        </ClickAwayListener>
      )}
      {/* modal 刪除 block 確認 */}
      <Dialog
        size="xs"
        color="error"
        title={t('canvas.modal.deleteBlock.title', {
          title: targetElement?.label,
        })}
        content={t('canvas.modal.deleteBlock.info1')}
        open={openModal === ModalTypesEnum.DELETE_BLOCK}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.delete')}
        handleConfirm={handleDeleteBlockModal}
      >
        {t('canvas.modal.deleteBlock.info2')}
      </Dialog>
      {/* modal 刪除 blocks 確認 */}
      <Dialog
        size="xs"
        color="error"
        title={t('canvas.modal.deleteBlocks.title', {
          number: selectedNodes.filter(
            (item) => item.type !== ElementTypesEnum.ENTRY_POINT,
          ).length,
        })}
        content={t('canvas.modal.deleteBlocks.info1')}
        open={openModal === ModalTypesEnum.DELETE_BLOCKS}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.delete')}
        handleConfirm={handleDeleteBlocksModal}
      >
        {t('canvas.modal.deleteBlocks.info2')}
      </Dialog>
      {/* modal 刪除 element 確認 */}
      <Dialog
        size="xs"
        color="error"
        title={t('canvas.modal.deleteElement.title', {
          title: targetElement?.elementName,
        })}
        content={t('canvas.modal.deleteElement.info1')}
        open={openModal === ModalTypesEnum.DELETE_ELEMENT}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.delete')}
        handleConfirm={handleDeleteElementModal}
      >
        {t('canvas.modal.deleteElement.info2')}
      </Dialog>
      {/* modal 刪除 text-button 確認 */}
      <Dialog
        size="xs"
        color="error"
        title={t('canvas.modal.deleteTextButton.title', {
          title: targetElement?.label,
        })}
        content={t('canvas.modal.deleteTextButton.info1')}
        open={openModal === ModalTypesEnum.DELETE_TEXT_BUTTON}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.delete')}
        handleConfirm={handleDeleteTextButtonModal}
      >
        {t('canvas.modal.deleteTextButton.info2')}
      </Dialog>
      {/* modal 提示 welcome message 尚未起用 */}
      <Dialog
        size="xs"
        title={t('canvas.modal.welcomeMessageDisable.title')}
        content={t('canvas.modal.welcomeMessageDisable.info')}
        open={openModal === ModalTypesEnum.WELCOME_MESSAGE_DISABLE}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.ok')}
        handleConfirm={gotoWelcomeMessage}
      />
      {/* modal 提示 persistent menu 尚未起用 */}
      <Dialog
        size="xs"
        title={t('canvas.modal.persistentMenuDisable.title')}
        content={t('canvas.modal.persistentMenuDisable.info')}
        open={openModal === ModalTypesEnum.PERSISTENT_MENU_DISABLE}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.ok')}
        handleConfirm={gotoPersistentMenu}
      />
      {/* modal add comment auto reply */}
      <Dialog
        size="xs"
        title={t('canvas.modal.addCommentAutoReply.title')}
        content={t('canvas.modal.addCommentAutoReply.content')}
        open={openModal === ModalTypesEnum.ADD_COMMENT_AUTO_REPLY}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('canvas.modal.addCommentAutoReply.confirm')}
        handleConfirm={handleAddCommentAutoReply}
      />
      {/* modal add persistent menu */}
      <Dialog
        size="xs"
        title={t('canvas.modal.addPersistentMenu.title')}
        content={t('canvas.modal.addPersistentMenu.content')}
        open={openModal === ModalTypesEnum.ADD_PERSISTENT_MENU}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.add')}
        handleConfirm={gotoPersistentMenu}
        confirmBtnID="trigger_menu_add"
      />
      {/* modal 提示 basic setting toggle 設定 */}
      <Dialog
        size="xs"
        title={t('canvas.modal.basicSettingToggle.title')}
        content={t('canvas.modal.basicSettingToggle.info')}
        open={openModal === ModalTypesEnum.BASIC_SETTING_TOGGLE}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('canvas.modal.basicSettingToggle.confirm')}
        handleConfirm={gotoBasicSetting}
      />
      {/* delete 共用 modal*/}
      <Dialog
        size="xs"
        color="error"
        title={modalState.title}
        content={modalState.content}
        open={openModal === ModalTypesEnum.DELETE}
        handleClose={closeModal}
        cancelBtnText={t('common.cancel')}
        confirmBtnText={t('common.delete')}
        handleConfirm={modalState.handleConfirm}
      >
        {modalState.info}
      </Dialog>
      <ConfirmCloseDrawerModal
        open={openModal === ModalTypesEnum.CLOSE_UNSAVED_DRAWER}
        handleCancel={closeModal}
        handleConfirm={handleCloseAndSaveDrawer}
        disableConfirmButton={!isDrawerFullyUpdate}
      />
      <CustomMarker />
      <CustomFocusMarker />
    </EditorCanvasContainer>
  );
};
